import React, { useCallback, useEffect, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import Loader from './Loader';
import { tableCustomStyles as appTableCustomStyles } from './Utility';

const defaultPaginationOptions = {
  rowsPerPageText: 'Rows per page:',
  rangeSeparatorText: 'of',
  selectAllRowsItem: false,
};

/**
 * Shared DataTable wrapper (react-data-table-component) with app-wide defaults.
 *
 * **`useAppDefaults` (default `true`)** — applies `customStyles` from Utility (`tableCustomStyles`),
 * `persistTableHead`, and default rows-per-page options unless overridden.
 *
 * **`stickyHeader`** — sets `fixedHeader` + default scroll height (`dataTableDefaults`) when you
 * do not pass `fixedHeader` / `fixedHeaderScrollHeight` yourself.
 *
 * **Managed server pagination** — `manageServerPagination` + `onPaginationChange(page, pageSize)` +
 * `totalRows` + `resetPaginationKey`.
 *
 * **Controlled server pagination** — `paginationServer` + `currentPage` + `pageSize` +
 * `onPageChange` + `onRowsPerPageChange`.
 *
 * **`loading`** → `progressPending` + default `Loader`.
 */
export const SERVER_PAGE_META_KEYS = ['CurrentPage', 'PageSize', 'TotalRecords', 'TotalPages'];

export function stripServerPageMetaFromRow(row, metaKeys = SERVER_PAGE_META_KEYS) {
  if (!row || typeof row !== 'object') return row;
  const next = { ...row };
  metaKeys.forEach((k) => {
    delete next[k];
  });
  return next;
}

export function parseServerPagedResponse(res) {
  if (!res) return { rows: [], total: 0 };
  if (Array.isArray(res)) {
    const first = res[0];
    const total =
      typeof first?.TotalRecords === 'number' ? first.TotalRecords :
        typeof first?.Count === 'number' ? first.Count :
          typeof first?.TotalCount === 'number' ? first.TotalCount :
            typeof first?.TotalRows === 'number' ? first.TotalRows :
              res.length;
    const rows = res.map((row) => stripServerPageMetaFromRow(row));
    return { rows, total };
  }
  const data = res?.Data ?? res?.data;
  if (Array.isArray(data)) {
    const first = data[0];
    const total =
      typeof res?.TotalRecords === 'number' ? res.TotalRecords :
        typeof res?.TotalCount === 'number' ? res.TotalCount :
          typeof res?.TotalRows === 'number' ? res.TotalRows :
            typeof first?.TotalRecords === 'number' ? first.TotalRecords :
              typeof first?.Count === 'number' ? first.Count :
                data.length;
    const rows = data.map((row) => stripServerPageMetaFromRow(row));
    return { rows, total };
  }
  return { rows: [], total: 0 };
}

function CommonDataTable({
  columns,
  data = [],
  loading = false,
  progressComponent,
  /** Apply shared `tableCustomStyles`, `persistTableHead`, and default page-size options */
  useAppDefaults = true,
  /** Shorthand for fixed header + default scroll height from `dataTableDefaults` */
  stickyHeader = false,
  customStyles: customStylesProp,
  persistTableHead: persistTableHeadProp,
  fixedHeader: fixedHeaderProp,
  fixedHeaderScrollHeight: fixedHeaderScrollHeightProp,
  manageServerPagination = false,
  resetPaginationKey,
  defaultPageSize = 10,
  onPaginationChange,
  paginationServer = false,
  serverPagination,
  currentPage: currentPageProp = 1,
  totalRows = 0,
  pageSize: pageSizeProp = 10,
  onPageChange,
  onRowsPerPageChange,
  paginationRowsPerPageOptions = [10, 20, 50, 100],
  paginationComponentOptions,
  dense = true,
  highlightOnHover = true,
  responsive = true,
  ...rest
}) {

  const {
    paginationServer: paginationServerFromRest,
    ...dataTableRest
  } = rest;

  const useServerPagination = Boolean(
    manageServerPagination || paginationServer || serverPagination || paginationServerFromRest,
  );

  const customStyles = customStylesProp ?? (useAppDefaults ? appTableCustomStyles : undefined);
  const persistTableHead = persistTableHeadProp !== undefined
    ? persistTableHeadProp
    : (useAppDefaults ? true : undefined);

  const fixedHeader = fixedHeaderProp !== undefined
    ? fixedHeaderProp
    : (stickyHeader ? true : undefined);

  const fixedHeaderScrollHeight =
    fixedHeaderProp === false
      ? undefined
      : fixedHeaderScrollHeightProp !== undefined
        ? fixedHeaderScrollHeightProp
        : stickyHeader && fixedHeader
          ? '400px'
          : undefined;

  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(defaultPageSize);

  const onPaginationChangeRef = useRef(onPaginationChange);
  onPaginationChangeRef.current = onPaginationChange;

  useEffect(() => {
    if (!manageServerPagination) return;
    if (resetPaginationKey == null || resetPaginationKey === '') return;
    setInternalPage(1);
    setInternalPageSize(defaultPageSize);
    onPaginationChangeRef.current?.(1, defaultPageSize);
  }, [manageServerPagination, resetPaginationKey, defaultPageSize]);

  const currentPage = manageServerPagination ? internalPage : currentPageProp;
  const pageSize = manageServerPagination ? internalPageSize : pageSizeProp;

  const handleManagedPageChange = useCallback(
    (page) => {
      const next = Number(page);
      if (!Number.isFinite(next) || next < 1 || next === internalPage) return;
      setInternalPage(next);
      onPaginationChangeRef.current?.(next, internalPageSize);
    },
    [internalPage, internalPageSize],
  );

  const handleManagedRowsPerPageChange = useCallback((newPerPage) => {
    const next = Number(newPerPage);
    if (!Number.isFinite(next) || next < 1 || next === internalPageSize) return;
    setInternalPageSize(next);
    setInternalPage(1);
    onPaginationChangeRef.current?.(1, next);
  }, [internalPageSize]);

  const serverProps = useServerPagination
    ? {
      paginationServer: true,
      paginationDefaultPage: currentPage,
      paginationTotalRows: totalRows,
      paginationPerPage: pageSize,
      onChangePage: manageServerPagination ? handleManagedPageChange : onPageChange,
      onChangeRowsPerPage: manageServerPagination ? handleManagedRowsPerPageChange : onRowsPerPageChange,
      paginationResetDefaultPage: false,
      paginationRowsPerPageOptions,
      paginationComponentOptions: {
        ...defaultPaginationOptions,
        ...paginationComponentOptions,
      },
    }
    : {};

  return (
    <DataTable
      columns={columns}
      data={Array.isArray(data) ? data : []}
      dense={dense}
      pagination
      highlightOnHover={highlightOnHover}
      responsive={responsive}
      progressPending={loading}
      progressComponent={progressComponent ?? <Loader />}
      customStyles={customStyles}
      {...(persistTableHead !== undefined ? { persistTableHead } : {})}
      {...(fixedHeader !== undefined ? { fixedHeader } : {})}
      {...(fixedHeaderScrollHeight !== undefined ? { fixedHeaderScrollHeight } : {})}
      {...serverProps}
      {...dataTableRest}
    />
  );
}

export default CommonDataTable;
